import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { SUPPORT_TYPES, supportService, SupportApiError, SupportMessage, SupportThread, SupportType } from '../../../services/support/supportService';
import { useSupportIdentity, useSupportRefresh, useSupportStore } from '../../../stores/appSupportStore';
import { borderGrey, cardBg, lightGreenBG, primaryText, redTextColor, secondaryText } from '../../../styling/stylingHelper';
import { SupportComposer, SupportDraft } from './SupportComposer';

export interface SupportConversationProps {
  threadId: string;
  admin?: boolean;
  onChanged?: () => void;
  onDraftChange?: (dirty: boolean) => void;
  onDeleted?: (attachmentCleanupPending: boolean) => void;
}

export function mergeSupportMessages(current: SupportMessage[], incoming: SupportMessage[]): SupportMessage[] {
  const byId = new Map(current.map(message => [message.id, message]));
  incoming.forEach(message => byId.set(message.id, message));
  return Array.from(byId.values()).sort((a, b) => Number(a.sequence) - Number(b.sequence));
}

/** Remount on any identity/thread/surface change, so drafts and async results cannot cross sessions. */
export function SupportConversation(props: SupportConversationProps) {
  const identity = useSupportIdentity();
  if (!identity) return null;
  return <Conversation key={`${identity}:${props.admin ? 'admin' : 'customer'}:${props.threadId}`} {...props} />;
}

function Conversation({ threadId, admin = false, onChanged, onDraftChange, onDeleted }: SupportConversationProps) {
  const { t, i18n } = useTranslation('support');
  const [thread, setThread] = useState<SupportThread | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [older, setOlder] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [newMessages, setNewMessages] = useState(false);
  const [resending, setResending] = useState<string | null>(null);
  const [deleteVersion, setDeleteVersion] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const mounted = useRef(true);
  const controller = useRef(new AbortController());
  const messageRef = useRef(messages);
  // A locally sent message can leap over concurrent messages we have not fetched yet.
  // Only server pagination advances the fetch cursor, never the displayed transcript.
  const lastFetchedSequence = useRef<string | undefined>(undefined);
  messageRef.current = messages;
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const prependHeight = useRef<number | null>(null);
  const reading = useRef(false);
  const acknowledgeRef = useRef<() => Promise<void>>(async () => {});
  const changed = useRef(onChanged);
  changed.current = onChanged;
  useEffect(() => { mounted.current = true; controller.current = new AbortController(); return () => { mounted.current = false; controller.current.abort(); }; }, []);

  const refresh = async () => {
    const last = lastFetchedSequence.current;
    const signal = controller.current.signal;
    try {
      const [header, page] = await Promise.all([
        supportService.getThread(threadId, admin, signal),
        supportService.listMessages(threadId, last ? { after: last } : {}, admin, signal),
      ]);
      if (!mounted.current || signal.aborted) return;
      setThread(current => !current || Number(header.version) >= Number(current.version) ? header : current);
      if (!last) setOlder(page.nextCursor);
      if (page.messages.length) {
        lastFetchedSequence.current = page.messages[page.messages.length - 1].sequence;
        setMessages(current => mergeSupportMessages(current, page.messages));
        if (last && !stickToBottom.current) setNewMessages(true);
      }
      setError(null);
      // Retry a previous failed read acknowledgement even when no messages arrived.
      void acknowledgeRef.current();
    } catch (failure) {
      if (mounted.current && !signal.aborted) setError(failure instanceof SupportApiError && [403, 404].includes(failure.status) ? 'unavailable' : 'loadFailed');
      throw failure;
    }
  };
  useSupportRefresh(refresh, 15_000, `${admin}:${threadId}`);

  const acknowledgeVisible = async () => {
    const viewport = scrollRef.current;
    if (!viewport || reading.current || document.visibilityState === 'hidden' || !mounted.current) return;
    const bounds = viewport.getBoundingClientRect();
    const ids = Array.from(viewport.querySelectorAll<HTMLElement>('[data-message-id]')).filter(element => {
      const box = element.getBoundingClientRect();
      return box.bottom > bounds.top && box.top < bounds.bottom && box.height > 0;
    }).map(element => element.dataset.messageId!).filter(id => messageRef.current.some(message => message.id === id && !message.isRead)).slice(0, 50);
    if (!ids.length) return;
    reading.current = true;
    try {
      await supportService.markRead(threadId, ids, admin);
      if (!mounted.current) return;
      setMessages(current => current.map(message => ids.includes(message.id) ? { ...message, isRead: true } : message));
      changed.current?.();
      if (!admin) void useSupportStore.getState().refreshUnread().catch(() => undefined);
    } catch { /* Leave the badge intact; the next foreground refresh retries visible messages. */ }
    finally { reading.current = false; }
  };
  acknowledgeRef.current = acknowledgeVisible;

  useLayoutEffect(() => {
    const viewport = scrollRef.current;
    if (!viewport) return;
    if (prependHeight.current !== null) { viewport.scrollTop += viewport.scrollHeight - prependHeight.current; prependHeight.current = null; }
    else if (stickToBottom.current) viewport.scrollTop = viewport.scrollHeight;
  }, [messages]);
  useEffect(() => {
    void acknowledgeRef.current();
    const onVisible = () => { void acknowledgeRef.current(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); };
  }, [messages]);

  const loadOlder = async () => {
    if (!older || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const page = await supportService.listMessages(threadId, { before: older }, admin, controller.current.signal);
      if (!mounted.current) return;
      prependHeight.current = scrollRef.current?.scrollHeight ?? null;
      stickToBottom.current = false;
      setMessages(current => mergeSupportMessages(current, page.messages)); setOlder(page.nextCursor);
    } catch { if (mounted.current) setError('loadFailed'); }
    finally { if (mounted.current) setLoadingOlder(false); }
  };
  const send = async (draft: SupportDraft) => {
    const result = await supportService.reply(threadId, draft, admin);
    if (!mounted.current) return;
    setThread(result.thread); setMessages(current => mergeSupportMessages(current, [result.message]));
    setNotice(result.emailStatus === 'failed' ? 'replySavedEmailFailed' : null);
    stickToBottom.current = true; setNewMessages(false); changed.current?.();
    if (!admin) void useSupportStore.getState().refreshUnread().catch(() => undefined);
  };
  const update = async (patch: { status?: 'resolved' | 'awaiting_support'; type?: SupportType }) => {
    if (!thread || updating) return;
    setUpdating(true); setNotice(null);
    try {
      const updated = await supportService.updateThread(threadId, { version: thread.version, ...patch }, admin);
      if (mounted.current) { setThread(updated); changed.current?.(); }
    } catch (failure) {
      if (mounted.current) setNotice(failure instanceof SupportApiError && failure.status === 409 ? 'changedRecently' : 'updateFailed');
      await refresh().catch(() => undefined);
    } finally { if (mounted.current) setUpdating(false); }
  };
  const download = async (messageId: string, attachmentId: string) => {
    // Open the window synchronously to retain the browser's user-gesture permission.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const { url } = await supportService.attachmentUrl(threadId, messageId, attachmentId, admin);
      if (!mounted.current) { tab?.close(); return; }
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch { tab?.close(); if (mounted.current) setNotice('downloadFailed'); }
  };
  const resend = async (messageId: string) => {
    if (resending || !window.confirm(t('admin.resendConfirm'))) return;
    setResending(messageId);
    try {
      const result = await supportService.resendEmail(threadId, messageId);
      if (mounted.current) { setNotice(result.emailStatus === 'sent' ? 'admin.resendSent' : 'admin.resendFailed'); changed.current?.(); }
    } catch { if (mounted.current) setNotice('admin.resendFailed'); }
    finally { if (mounted.current) setResending(null); }
  };
  const deleteThread = async () => {
    if (!admin || !deleteVersion || deleting) return;
    setDeleting(true); setNotice(null);
    try {
      const result = await supportService.deleteThread(threadId, deleteVersion);
      if (!mounted.current) return;
      controller.current.abort();
      onDraftChange?.(false);
      onDeleted?.(result.attachmentCleanupPending);
      setError('unavailable');
    } catch (failure) {
      if (!mounted.current) return;
      setNotice(failure instanceof SupportApiError && failure.status === 409 ? 'changedRecently' : 'admin.deleteFailed');
      setDeleteVersion(null);
      await refresh().catch(() => undefined);
    } finally { if (mounted.current) setDeleting(false); }
  };
  const button = cn('min-h-[44px] rounded-xl border px-3 py-2 text-sm disabled:opacity-50', borderGrey, primaryText);
  if (error === 'unavailable') return <p role="alert" className={cn('p-5', secondaryText)}>{t(error)}</p>;
  return <section className={cn('flex h-full min-h-0 flex-col', primaryText)} aria-label={t('conversation')}>
    <header className={cn('shrink-0 space-y-2 border-b px-4 py-3', borderGrey)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="line-clamp-2 break-words text-base font-semibold" title={thread?.subject}>{thread?.subject ?? t('loading')}</h2>
          {thread && <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className={cn('rounded-full border px-2 py-0.5 text-xs font-medium', borderGrey, lightGreenBG)}>{t(`types.${thread.type}`)}</span>
            {thread.status === 'resolved' && <span className={cn('text-xs', secondaryText)}>{t('statuses.resolved')}</span>}
            {thread.restaurantName && <span className={cn('break-words text-xs', secondaryText)}>{thread.restaurantName}</span>}
          </div>}
        </div>
        {thread && <button type="button" disabled={updating || deleteVersion !== null} className={cn(button, 'shrink-0')}
          aria-label={t(thread.status === 'resolved' ? 'reopen' : 'resolve')}
          onClick={() => void update({ status: thread.status === 'resolved' ? 'awaiting_support' : 'resolved' })}>{t(thread.status === 'resolved' ? 'reopenShort' : 'resolveShort')}</button>}
      </div>
      {thread?.recipientUserIds && <p className={cn('text-xs', secondaryText)}>{t('admin.recipients')}: {thread.recipients?.map(user => user.name || t('admin.formerUser')).join(', ')} · {t('admin.ownerVisibility')}</p>}
      {thread && <>
        {admin && <div className="flex flex-wrap items-center gap-2">
          <select aria-label={t('typeLabel')} className={cn(button, cardBg)} value={thread.type} disabled={updating || deleteVersion !== null} onChange={event => void update({ type: event.target.value as SupportType })}>
            {SUPPORT_TYPES.map(type => <option key={type} value={type}>{t(`types.${type}`)}</option>)}
          </select>
          {!deleteVersion && <button type="button" disabled={updating} className={cn(button, redTextColor)} onClick={() => setDeleteVersion(thread.version)}>{t('admin.delete')}</button>}
        </div>}
        {admin && deleteVersion && <div className="space-y-2" role="group" aria-label={t('admin.delete')}>
          <p className={cn('text-sm', redTextColor)}>{t('admin.deleteWarning')}</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={deleting} className={cn(button, redTextColor)} onClick={() => void deleteThread()}>{t(deleting ? 'admin.deleting' : 'admin.deleteConfirm')}</button>
            <button type="button" disabled={deleting} className={button} onClick={() => setDeleteVersion(null)}>{t('admin.deleteCancel')}</button>
          </div>
        </div>}
      </>}
      {error && <div role="alert" className={cn('text-sm', redTextColor)}>{t(error)} <button type="button" className="underline" onClick={() => void refresh().catch(() => undefined)}>{t('retry')}</button></div>}
      {notice && <p role="status" className={cn('text-sm', secondaryText)}>{t(notice)}</p>}
    </header>
    <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-3 sm:px-4" onScroll={() => {
      const element = scrollRef.current!;
      stickToBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
      if (stickToBottom.current) setNewMessages(false);
      void acknowledgeVisible();
    }}>
      {older && <button type="button" className={button} disabled={loadingOlder} onClick={() => void loadOlder()}>{t(loadingOlder ? 'loading' : 'olderMessages')}</button>}
      {messages.map(message => <article key={message.id} data-message-id={message.id} className={cn('rounded-xl border p-3', borderGrey, message.senderSide === 'support' ? lightGreenBG : cardBg)}>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-1 text-xs">
          <span className="break-words font-semibold">{message.senderSide === 'support' ? t('supportSender') : message.effectiveName}</span>
          <time className={secondaryText} dateTime={message.createdAt}>{new Date(message.createdAt).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}</time>
        </div>
        <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
        {admin && message.senderSide === 'support' && <button type="button" className={cn(button, 'mt-3')} disabled={resending !== null || deleteVersion !== null} onClick={() => void resend(message.id)}>{t(resending === message.id ? 'sending' : 'admin.resendEmail')}</button>}
        {message.attachments.length > 0 && <ul className="mt-3 space-y-2">{message.attachments.map(file => <li key={file.id}><button type="button" className={cn(button, 'flex max-w-full items-center gap-2 text-left')} onClick={() => void download(message.id, file.id)}>
          <Download size={16} className="shrink-0" aria-hidden="true" /><span className="min-w-0 break-all">{file.filename} · {(file.sizeBytes / 1024).toFixed(0)} {t('kilobytes')}</span>
        </button></li>)}</ul>}
      </article>)}
    </div>
    {newMessages && <button type="button" className={cn(button, 'mx-4 my-2 shrink-0')} onClick={() => { stickToBottom.current = true; setNewMessages(false); if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; void acknowledgeVisible(); }}>{t('newMessages')}</button>}
    <div className={cn('max-h-[40%] shrink-0 overflow-y-auto border-t p-3 sm:px-4', borderGrey)}>
      <SupportComposer onSend={send} onDraftChange={onDraftChange} disabled={!thread || deleteVersion !== null} />
    </div>
  </section>;
}
